import { API_BASE } from '../../config/apiBase';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { 
  ArrowLeft, Save, Edit, BookOpen, FileText, 
  CheckCircle, Languages, Eye, Plus, Upload, Video, Trash2, Loader2, RefreshCw,
  ChevronUp, ChevronDown, Globe, Wand2, GraduationCap, Trophy
} from 'lucide-react';

const API = API_BASE;

export default function TrainingContentEditor() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState({ es: false, en: false });
  const [uploadProgress, setUploadProgress] = useState({ es: 0, en: 0 });
  const fileInputRefEs = useRef(null);
  const fileInputRefEn = useRef(null);
  const [editingContent, setEditingContent] = useState({
    content_en: '',
    content_es: '',
    title_en: '',
    title_es: '',
    video_url_en: '',
    video_url_es: ''
  });
  const [activeTab, setActiveTab] = useState('es');
  
  // States for actions
  const [translating, setTranslating] = useState(false);
  const [translatingAll, setTranslatingAll] = useState(false);
  // Quiz regeneration states removed - now managed via Assessment Builder
  const [deleting, setDeleting] = useState(false);
  
  // New: States for creating modules and lessons
  const [createModuleDialog, setCreateModuleDialog] = useState(false);
  const [createLessonDialog, setCreateLessonDialog] = useState(false);
  const [newModule, setNewModule] = useState({
    title_en: '',
    title_es: '',
    description_en: '',
    description_es: ''
  });
  const [newLesson, setNewLesson] = useState({
    title_en: '',
    title_es: '',
    content_en: '',
    content_es: ''
  });
  const [creatingModule, setCreatingModule] = useState(false);
  const [creatingLesson, setCreatingLesson] = useState(false);
  const [reinitializing, setReinitializing] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchModules();
  }, [user, navigate]);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${API}/training/admin/modules`);
      setModules(response.data);
    } catch (error) {
      console.error('Error fetching modules:', error);
      toast.error('Error al cargar módulos');
    } finally {
      setLoading(false);
    }
  };

  const fetchLessons = async (moduleId) => {
    try {
      console.log('Fetching lessons for module:', moduleId);
      const response = await axios.get(`${API}/training/admin/modules/${moduleId}/lessons`);
      console.log('Lessons response:', response.data);
      setLessons(response.data || []);
      if (response.data?.length === 0) {
        console.warn('No lessons found for module:', moduleId);
      }
    } catch (error) {
      console.error('Error fetching lessons:', error);
      toast.error('Error al cargar lecciones: ' + (error.response?.data?.detail || error.message));
      setLessons([]);
    }
  };

  const handleModuleSelect = (moduleId) => {
    const module = modules.find(m => m.id === moduleId);
    setSelectedModule(module);
    setSelectedLesson(null);
    fetchLessons(moduleId);
  };

  const handleLessonSelect = (lesson) => {
    setSelectedLesson(lesson);
    setEditingContent({
      content_en: lesson.content_en || '',
      content_es: lesson.content_es || '',
      title_en: lesson.title_en || '',
      title_es: lesson.title_es || '',
      video_url_en: lesson.video_url_en || '',
      video_url_es: lesson.video_url_es || ''
    });
  };

  const handleEditLesson = () => {
    if (!selectedLesson) return;
    setEditDialog(true);
  };

  const handleSaveLesson = async () => {
    if (!selectedLesson) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/training/admin/lessons/${selectedLesson.id}`, {
        title_en: editingContent.title_en,
        title_es: editingContent.title_es,
        content_en: editingContent.content_en,
        content_es: editingContent.content_es,
        video_url_en: editingContent.video_url_en || null,
        video_url_es: editingContent.video_url_es || null
      });
      
      toast.success('Contenido guardado exitosamente');
      setEditDialog(false);
      
      // Refresh lessons
      if (selectedModule) {
        fetchLessons(selectedModule.id);
      }
      
      // Update selected lesson
      setSelectedLesson({
        ...selectedLesson,
        ...editingContent
      });
    } catch (error) {
      console.error('Error saving lesson:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleVideoUpload = async (file, language) => {
    if (!file || !selectedLesson) return;
    
    // Validate file type
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato no válido. Use MP4, WebM o MOV.');
      return;
    }
    
    // Validate file size (500MB max)
    const maxSize = 500 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('El archivo es muy grande. Máximo 500MB.');
      return;
    }
    
    setUploadingVideo(prev => ({ ...prev, [language]: true }));
    setUploadProgress(prev => ({ ...prev, [language]: 0 }));
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('lesson_id', selectedLesson.id);
      formData.append('language', language);
      
      const response = await axios.post(`${API}/training/admin/upload-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, [language]: progress }));
        }
      });
      
      // Update local state
      const videoUrlKey = `video_url_${language}`;
      setEditingContent(prev => ({
        ...prev,
        [videoUrlKey]: response.data.video_url
      }));
      setSelectedLesson(prev => ({
        ...prev,
        [videoUrlKey]: response.data.video_url
      }));
      
      toast.success(`Video (${language.toUpperCase()}) subido exitosamente`);
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error(error.response?.data?.detail || 'Error al subir video');
    } finally {
      setUploadingVideo(prev => ({ ...prev, [language]: false }));
      setUploadProgress(prev => ({ ...prev, [language]: 0 }));
    }
  };

  const handleRemoveVideo = async (language) => {
    if (!selectedLesson) return;
    
    const videoUrlKey = `video_url_${language}`;
    setEditingContent(prev => ({
      ...prev,
      [videoUrlKey]: ''
    }));
    
    toast.success(`URL de video (${language.toUpperCase()}) eliminada`);
  };

  // Create new module
  const handleCreateModule = async () => {
    if (!newModule.title_es || !newModule.title_en) {
      toast.error('Ingresa el título en ambos idiomas');
      return;
    }
    
    setCreatingModule(true);
    try {
      const response = await axios.post(`${API}/training/admin/modules`, {
        title_en: newModule.title_en,
        title_es: newModule.title_es,
        description_en: newModule.description_en || 'Module description',
        description_es: newModule.description_es || 'Descripción del módulo',
        module_number: modules.length + 1,
        order: modules.length + 1,
        estimated_hours: 2,
        is_active: true
      });
      
      toast.success('Módulo creado exitosamente');
      setCreateModuleDialog(false);
      setNewModule({ title_en: '', title_es: '', description_en: '', description_es: '' });
      fetchModules();
    } catch (error) {
      console.error('Error creating module:', error);
      toast.error(error.response?.data?.detail || 'Error al crear módulo');
    } finally {
      setCreatingModule(false);
    }
  };

  // Create new lesson
  const handleCreateLesson = async () => {
    if (!selectedModule) {
      toast.error('Selecciona un módulo primero');
      return;
    }
    if (!newLesson.title_es || !newLesson.title_en) {
      toast.error('Ingresa el título en ambos idiomas');
      return;
    }
    
    setCreatingLesson(true);
    try {
      const response = await axios.post(`${API}/training/admin/lessons`, {
        module_id: selectedModule.id,
        title_en: newLesson.title_en,
        title_es: newLesson.title_es,
        content_en: newLesson.content_en || '<p>Content here</p>',
        content_es: newLesson.content_es || '<p>Contenido aquí</p>',
        lesson_number: lessons.length + 1,
        order: lessons.length + 1,
        type: 'text',
        duration_minutes: 15,
        is_active: true
      });
      
      toast.success('Lección creada exitosamente');
      setCreateLessonDialog(false);
      setNewLesson({ title_en: '', title_es: '', content_en: '', content_es: '' });
      fetchLessons(selectedModule.id);
    } catch (error) {
      console.error('Error creating lesson:', error);
      toast.error(error.response?.data?.detail || 'Error al crear lección');
    } finally {
      setCreatingLesson(false);
    }
  };

  // Delete module
  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('¿Estás seguro de eliminar este módulo y todas sus lecciones?')) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/training/admin/modules/${moduleId}`);
      toast.success('Módulo eliminado');
      setSelectedModule(null);
      setLessons([]);
      fetchModules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar módulo');
    } finally {
      setDeleting(false);
    }
  };

  // Delete lesson
  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta lección?')) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/training/admin/lessons/${lessonId}`);
      toast.success('Lección eliminada');
      setSelectedLesson(null);
      if (selectedModule) fetchLessons(selectedModule.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar lección');
    } finally {
      setDeleting(false);
    }
  };

  // Reorder modules
  const handleReorderModule = async (moduleId, direction) => {
    const index = modules.findIndex(m => m.id === moduleId);
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === modules.length - 1)) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const newModules = [...modules];
    [newModules[index], newModules[newIndex]] = [newModules[newIndex], newModules[index]];
    
    const reorderItems = newModules.map((m, i) => ({ id: m.id, order: i + 1 }));
    
    try {
      await axios.put(`${API}/training/admin/modules/reorder`, { items: reorderItems });
      setModules(newModules.map((m, i) => ({ ...m, order: i + 1, module_number: i + 1 })));
      toast.success('Módulos reordenados');
    } catch (error) {
      toast.error('Error al reordenar módulos');
    }
  };

  // Reorder lessons
  const handleReorderLesson = async (lessonId, direction) => {
    const index = lessons.findIndex(l => l.id === lessonId);
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === lessons.length - 1)) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const newLessons = [...lessons];
    [newLessons[index], newLessons[newIndex]] = [newLessons[newIndex], newLessons[index]];
    
    const reorderItems = newLessons.map((l, i) => ({ id: l.id, order: i + 1 }));
    
    try {
      await axios.put(`${API}/training/admin/lessons/reorder`, { items: reorderItems });
      setLessons(newLessons.map((l, i) => ({ ...l, order: i + 1, lesson_number: i + 1 })));
      toast.success('Lecciones reordenadas');
    } catch (error) {
      toast.error('Error al reordenar lecciones');
    }
  };

  // Translate single lesson
  const handleTranslateLesson = async (lessonId) => {
    setTranslating(true);
    try {
      const response = await axios.post(`${API}/training/admin/lessons/${lessonId}/translate`);
      toast.success('Lección traducida exitosamente');
      if (selectedModule) fetchLessons(selectedModule.id);
      if (selectedLesson?.id === lessonId) {
        setEditingContent(prev => ({
          ...prev,
          title_en: response.data.title_en,
          content_en: response.data.content_en_preview
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al traducir lección');
    } finally {
      setTranslating(false);
    }
  };

  // Translate all content
  const handleTranslateAll = async () => {
    if (!window.confirm('¿Traducir todo el contenido de español a inglés? Esto puede tomar varios minutos.')) return;
    
    setTranslatingAll(true);
    toast.info('Traduciendo contenido... esto puede tomar varios minutos');
    try {
      const response = await axios.post(`${API}/training/admin/translate-all`);
      toast.success(`Traducción completada: ${response.data.translated_lessons} lecciones`);
      fetchModules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al traducir contenido');
    } finally {
      setTranslatingAll(false);
    }
  };

  // Regenerate quizzes removed - now managed via Assessment Builder
  
  // Regenerate only final exam - now managed via Assessment Builder

  const handleReinitializeContent = async (force = false) => {
    const endpoint = force 
      ? `${API}/training/admin/force-reinitialize-content`
      : `${API}/training/admin/reinitialize-content`;
    
    if (force) {
      const confirmed = window.confirm(
        '⚠️ ADVERTENCIA: Esto eliminará TODO el contenido existente y lo reemplazará con el contenido predeterminado.\n\n¿Está seguro que desea continuar?'
      );
      if (!confirmed) return;
    }
    
    setReinitializing(true);
    try {
      const response = await axios.post(endpoint);
      if (response.data.success) {
        toast.success(`Contenido inicializado: ${response.data.modules_count} módulos, ${response.data.lessons_count} lecciones`);
        fetchModules();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      console.error('Error reinitializing content:', error);
      toast.error('Error al reinicializar contenido: ' + (error.response?.data?.detail || error.message));
    } finally {
      setReinitializing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Hidden file inputs */}
      <input
        type="file"
        ref={fileInputRefEs}
        accept="video/mp4,video/webm,video/quicktime"
        className="hidden"
        onChange={(e) => handleVideoUpload(e.target.files[0], 'es')}
      />
      <input
        type="file"
        ref={fileInputRefEn}
        accept="video/mp4,video/webm,video/quicktime"
        className="hidden"
        onChange={(e) => handleVideoUpload(e.target.files[0], 'en')}
      />
      
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/training')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Editor de Contenido</h1>
              <p className="text-muted-foreground">Administra el contenido de entrenamiento</p>
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 p-4 bg-slate-50 rounded-lg border">
          <Button 
            variant="outline" 
            onClick={handleTranslateAll}
            disabled={translatingAll}
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
          >
            {translatingAll ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Traduciendo...
              </>
            ) : (
              <>
                <Globe className="h-4 w-4 mr-2" />
                Traducir Todo (ES → EN)
              </>
            )}
          </Button>
          
          {/* Quiz/Exam regeneration buttons removed - now managed via Assessment Builder */}
          
          <Button 
            variant="outline" 
            onClick={() => handleReinitializeContent(true)}
            disabled={reinitializing}
            className="border-orange-500 text-orange-600 hover:bg-orange-50"
          >
            {reinitializing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Reinicializando...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Reinicializar
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Module Selector */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Módulos
              </CardTitle>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => setCreateModuleDialog(true)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {modules.length === 0 ? (
              <div className="text-center py-8 space-y-4">
                <p className="text-muted-foreground text-sm">No hay módulos configurados</p>
                <Button 
                  onClick={() => handleReinitializeContent(false)}
                  disabled={reinitializing}
                  className="w-full"
                >
                  {reinitializing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Inicializando...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Inicializar Contenido
                    </>
                  )}
                </Button>
              </div>
            ) : (
              modules.map((module, index) => (
                <div 
                  key={module.id}
                  className={`group relative p-3 rounded-lg transition-colors ${
                    selectedModule?.id === module.id 
                      ? 'bg-primary text-primary-foreground' 
                      : 'hover:bg-muted'
                  }`}
                >
                  <button
                    onClick={() => handleModuleSelect(module.id)}
                    className="w-full text-left"
                  >
                    <div className="font-medium">Módulo {module.module_number || index + 1}</div>
                    <div className="text-sm opacity-80 truncate">{module.title_es}</div>
                  </button>
                  
                  {/* Reorder and Delete buttons - always visible (mobile no hover) */}
                  <div className="absolute right-1 top-1 flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={(e) => { e.stopPropagation(); handleReorderModule(module.id, 'up'); }}
                      disabled={index === 0}
                    >
                      <ChevronUp className="h-3 w-3" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={(e) => { e.stopPropagation(); handleReorderModule(module.id, 'down'); }}
                      disabled={index === modules.length - 1}
                    >
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => { e.stopPropagation(); handleDeleteModule(module.id); }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Lesson List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Lecciones
              </CardTitle>
              {selectedModule && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setCreateLessonDialog(true)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              )}
            </div>
            {selectedModule && (
              <CardDescription>{selectedModule.title_es}</CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
            {!selectedModule ? (
              <p className="text-muted-foreground text-sm">Selecciona un módulo</p>
            ) : lessons.length === 0 ? (
              <p className="text-muted-foreground text-sm">Sin lecciones</p>
            ) : (
              lessons.map((lesson, index) => (
                <div
                  key={lesson.id}
                  className={`group relative p-3 rounded-lg transition-colors ${
                    selectedLesson?.id === lesson.id 
                      ? 'bg-primary text-primary-foreground' 
                      : 'hover:bg-muted'
                  }`}
                >
                  <button
                    onClick={() => handleLessonSelect(lesson)}
                    className="w-full text-left"
                  >
                    <div className="flex items-center justify-between pr-24">
                      <span className="text-sm font-medium truncate flex-1">
                        {lesson.lesson_number || index + 1}. {lesson.title_es || lesson.title_en}
                      </span>
                      {lesson.content_es && lesson.content_es.length > 200 && (
                        <CheckCircle className="h-4 w-4 text-green-500 shrink-0 ml-2" />
                      )}
                    </div>
                  </button>
                  
                  {/* Action buttons - always visible (mobile devices have no hover state) */}
                  <div className="absolute right-1 top-1 flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={(e) => { e.stopPropagation(); handleReorderLesson(lesson.id, 'up'); }}
                      disabled={index === 0}
                      title="Subir"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={(e) => { e.stopPropagation(); handleReorderLesson(lesson.id, 'down'); }}
                      disabled={index === lessons.length - 1}
                      title="Bajar"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                      onClick={(e) => { e.stopPropagation(); handleTranslateLesson(lesson.id); }}
                      disabled={translating}
                      title="Traducir ES → EN"
                    >
                      <Globe className="h-3 w-3" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => { e.stopPropagation(); handleDeleteLesson(lesson.id); }}
                      title="Eliminar"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Content Preview & Editor */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Vista Previa
              </CardTitle>
              {selectedLesson && (
                <Button onClick={handleEditLesson}>
                  <Edit className="h-4 w-4 mr-2" />
                  Editar
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!selectedLesson ? (
              <div className="text-center text-muted-foreground py-12">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Selecciona una lección para ver y editar su contenido</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label className="text-muted-foreground">Título (Español)</Label>
                  <h3 className="text-lg font-semibold">{selectedLesson.title_es}</h3>
                </div>
                <div>
                  <Label className="text-muted-foreground">Título (English)</Label>
                  <h3 className="text-lg font-semibold">{selectedLesson.title_en}</h3>
                </div>
                
                <Tabs defaultValue="es" className="mt-4">
                  <TabsList>
                    <TabsTrigger value="es">
                      <Languages className="h-4 w-4 mr-2" />
                      Español
                    </TabsTrigger>
                    <TabsTrigger value="en">
                      <Languages className="h-4 w-4 mr-2" />
                      English
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="es" className="mt-4">
                    <div 
                      className="prose prose-sm max-w-none max-h-[400px] overflow-y-auto p-4 border rounded-lg bg-muted/20"
                      dangerouslySetInnerHTML={{ __html: selectedLesson.content_es || '<p class="text-muted-foreground">Sin contenido</p>' }}
                    />
                  </TabsContent>
                  <TabsContent value="en" className="mt-4">
                    <div 
                      className="prose prose-sm max-w-none max-h-[400px] overflow-y-auto p-4 border rounded-lg bg-muted/20"
                      dangerouslySetInnerHTML={{ __html: selectedLesson.content_en || '<p class="text-muted-foreground">No content</p>' }}
                    />
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Lección</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Titles */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Título (Español)</Label>
                <Input
                  value={editingContent.title_es}
                  onChange={(e) => setEditingContent({...editingContent, title_es: e.target.value})}
                  placeholder="Título en español"
                />
              </div>
              <div className="space-y-2">
                <Label>Título (English)</Label>
                <Input
                  value={editingContent.title_en}
                  onChange={(e) => setEditingContent({...editingContent, title_en: e.target.value})}
                  placeholder="Title in English"
                />
              </div>
            </div>

            {/* Video Upload Section */}
            <div className="space-y-4 p-4 border rounded-lg bg-gradient-to-br from-blue-50/50 to-purple-50/50">
              <h4 className="font-semibold flex items-center gap-2">
                <Video className="h-4 w-4" />
                Videos de la Lección
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Spanish Video */}
                <div className="space-y-3 p-3 border rounded-lg bg-white/70">
                  <Label className="flex items-center gap-2">
                    🇪🇸 Video (Español)
                  </Label>
                  
                  {editingContent.video_url_es ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-green-700 truncate flex-1">
                          {editingContent.video_url_es}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleRemoveVideo('es')}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                      <video 
                        src={editingContent.video_url_es.startsWith('/api') 
                          ? `${BACKEND_URL}${editingContent.video_url_es}`
                          : editingContent.video_url_es
                        } 
                        className="w-full h-32 object-cover rounded"
                        controls
                      />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Input
                        value={editingContent.video_url_es || ''}
                        onChange={(e) => setEditingContent({...editingContent, video_url_es: e.target.value})}
                        placeholder="URL del video o sube uno"
                      />
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full"
                        onClick={() => fileInputRefEs.current?.click()}
                        disabled={uploadingVideo.es}
                      >
                        {uploadingVideo.es ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Subiendo... {uploadProgress.es}%
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            Subir Video
                          </>
                        )}
                      </Button>
                      {uploadingVideo.es && (
                        <Progress value={uploadProgress.es} className="h-2" />
                      )}
                    </div>
                  )}
                </div>
                
                {/* English Video */}
                <div className="space-y-3 p-3 border rounded-lg bg-white/70">
                  <Label className="flex items-center gap-2">
                    🇺🇸 Video (English)
                  </Label>
                  
                  {editingContent.video_url_en ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-green-700 truncate flex-1">
                          {editingContent.video_url_en}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleRemoveVideo('en')}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                      <video 
                        src={editingContent.video_url_en.startsWith('/api') 
                          ? `${BACKEND_URL}${editingContent.video_url_en}`
                          : editingContent.video_url_en
                        } 
                        className="w-full h-32 object-cover rounded"
                        controls
                      />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Input
                        value={editingContent.video_url_en || ''}
                        onChange={(e) => setEditingContent({...editingContent, video_url_en: e.target.value})}
                        placeholder="Video URL or upload one"
                      />
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full"
                        onClick={() => fileInputRefEn.current?.click()}
                        disabled={uploadingVideo.en}
                      >
                        {uploadingVideo.en ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Uploading... {uploadProgress.en}%
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            Upload Video
                          </>
                        )}
                      </Button>
                      {uploadingVideo.en && (
                        <Progress value={uploadProgress.en} className="h-2" />
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground">
                Formatos soportados: MP4, WebM, MOV. Tamaño máximo: 500MB.
                Los videos aparecerán en la lección según el idioma del trainee.
              </p>
            </div>

            {/* Content Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="es">Contenido (Español)</TabsTrigger>
                <TabsTrigger value="en">Content (English)</TabsTrigger>
              </TabsList>
              
              <TabsContent value="es" className="mt-4">
                <div className="space-y-2">
                  <Label>Contenido HTML (Español)</Label>
                  <Textarea
                    value={editingContent.content_es}
                    onChange={(e) => setEditingContent({...editingContent, content_es: e.target.value})}
                    placeholder="<h2>Título</h2><p>Contenido...</p>"
                    className="min-h-[300px] font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Usa HTML básico: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;. 
                    Recuerda usar puntos al final de cada oración para pausas del TTS.
                  </p>
                </div>
              </TabsContent>
              
              <TabsContent value="en" className="mt-4">
                <div className="space-y-2">
                  <Label>HTML Content (English)</Label>
                  <Textarea
                    value={editingContent.content_en}
                    onChange={(e) => setEditingContent({...editingContent, content_en: e.target.value})}
                    placeholder="<h2>Title</h2><p>Content...</p>"
                    className="min-h-[300px] font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Use basic HTML: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;.
                    Remember to use periods at the end of sentences for TTS pauses.
                  </p>
                </div>
              </TabsContent>
            </Tabs>

            {/* Preview */}
            <div className="space-y-2">
              <Label>Vista Previa</Label>
              <div 
                className="prose prose-sm max-w-none max-h-[200px] overflow-y-auto p-4 border rounded-lg bg-muted/20"
                dangerouslySetInnerHTML={{ 
                  __html: activeTab === 'es' ? editingContent.content_es : editingContent.content_en 
                }}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveLesson} disabled={saving}>
              {saving ? (
                <>Guardando...</>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Guardar Cambios
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Module Dialog */}
      <Dialog open={createModuleDialog} onOpenChange={setCreateModuleDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Crear Nuevo Módulo
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Tabs defaultValue="es">
              <TabsList className="w-full">
                <TabsTrigger value="es" className="flex-1">Español</TabsTrigger>
                <TabsTrigger value="en" className="flex-1">English</TabsTrigger>
              </TabsList>
              
              <TabsContent value="es" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Título (Español) *</Label>
                  <Input
                    value={newModule.title_es}
                    onChange={(e) => setNewModule({...newModule, title_es: e.target.value})}
                    placeholder="Ej: Introducción al Financiamiento"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descripción (Español)</Label>
                  <Textarea
                    value={newModule.description_es}
                    onChange={(e) => setNewModule({...newModule, description_es: e.target.value})}
                    placeholder="Descripción breve del módulo..."
                    rows={3}
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="en" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Title (English) *</Label>
                  <Input
                    value={newModule.title_en}
                    onChange={(e) => setNewModule({...newModule, title_en: e.target.value})}
                    placeholder="E.g.: Introduction to Financing"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description (English)</Label>
                  <Textarea
                    value={newModule.description_en}
                    onChange={(e) => setNewModule({...newModule, description_en: e.target.value})}
                    placeholder="Brief module description..."
                    rows={3}
                  />
                </div>
              </TabsContent>
            </Tabs>
            
            <p className="text-xs text-muted-foreground">
              El módulo se creará con el número {modules.length + 1}. 
              Puedes agregar lecciones después de crearlo.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModuleDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateModule} disabled={creatingModule}>
              {creatingModule ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creando...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear Módulo
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Lesson Dialog */}
      <Dialog open={createLessonDialog} onOpenChange={setCreateLessonDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Crear Nueva Lección
            </DialogTitle>
            {selectedModule && (
              <p className="text-sm text-muted-foreground">
                En: Módulo {selectedModule.module_number} - {selectedModule.title_es}
              </p>
            )}
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Tabs defaultValue="es">
              <TabsList className="w-full">
                <TabsTrigger value="es" className="flex-1">Español</TabsTrigger>
                <TabsTrigger value="en" className="flex-1">English</TabsTrigger>
              </TabsList>
              
              <TabsContent value="es" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Título (Español) *</Label>
                  <Input
                    value={newLesson.title_es}
                    onChange={(e) => setNewLesson({...newLesson, title_es: e.target.value})}
                    placeholder="Ej: Conceptos Básicos"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contenido Inicial (Español)</Label>
                  <Textarea
                    value={newLesson.content_es}
                    onChange={(e) => setNewLesson({...newLesson, content_es: e.target.value})}
                    placeholder="<p>Contenido inicial...</p>"
                    rows={4}
                    className="font-mono text-sm"
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="en" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Title (English) *</Label>
                  <Input
                    value={newLesson.title_en}
                    onChange={(e) => setNewLesson({...newLesson, title_en: e.target.value})}
                    placeholder="E.g.: Basic Concepts"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Initial Content (English)</Label>
                  <Textarea
                    value={newLesson.content_en}
                    onChange={(e) => setNewLesson({...newLesson, content_en: e.target.value})}
                    placeholder="<p>Initial content...</p>"
                    rows={4}
                    className="font-mono text-sm"
                  />
                </div>
              </TabsContent>
            </Tabs>
            
            <p className="text-xs text-muted-foreground">
              La lección se creará con el número {lessons.length + 1}. 
              Puedes editar el contenido completo y agregar videos después.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateLessonDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateLesson} disabled={creatingLesson}>
              {creatingLesson ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creando...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear Lección
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
